'use client';

import { useCallback, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';

import { setSettingsCookie } from '@/utils/cookie';
import {
  DEFAULT_THEME_SETTINGS,
  type SettingsCookies,
  SettingsValueProps,
} from '@/utils/settings-theme';

import { SettingsContext } from './settings-context';

type SettingsProviderProps = {
  children: React.ReactNode;
  initialSettings: SettingsCookies;
};

export function SettingsProvider({ children, initialSettings }: SettingsProviderProps) {
  const { tableInitialWidths, ...themeSettings } = initialSettings;
  const [settings, setSettings] = useState<SettingsValueProps>(themeSettings);

  const update = useCallback(
    <K extends keyof SettingsValueProps>(name: K, value: SettingsValueProps[K]) => {
      setSettingsCookie(name, value);
      setSettings((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const reset = useCallback(() => {
    Object.entries(DEFAULT_THEME_SETTINGS).forEach(([key, value]) => {
      setSettingsCookie(key as keyof SettingsValueProps, value);
    });
    setSettings(DEFAULT_THEME_SETTINGS);
  }, []);

  const [openDrawer, setOpenDrawer] = useState(false);

  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual(settings, DEFAULT_THEME_SETTINGS);

  const memoizedValue = useMemo(
    () => ({
      onUpdate: update,
      canReset,
      onReset: reset,
      open: openDrawer,
      onToggle: onToggleDrawer,
      onClose: onCloseDrawer,
      tableInitialWidths,
      ...settings,
    }),
    [
      reset,
      update,
      settings,
      canReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      tableInitialWidths,
    ],
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
